/**
 * air_targets.ts — the shootable-aircraft registry (the structure_solids
 * pattern: physics-free, globalThis-backed so vite's dev-graph module
 * duplication can't split it).
 *
 * WarAirModule OWNS the entries (writes positions every frame, reads hp back
 * to run deaths); AtlasVehicleModule's bolt loop QUERIES them — a player bolt
 * entering a target's sphere calls `damageAirTarget`, which also latches
 * `hitByPlayer` on the side (the return-fire trigger: neither side is hostile
 * to the player until the player picks one).
 */
import type * as THREE from 'three/webgpu';

export interface AirTarget {
  id: string;
  /** War battles: the battle row id. Cargo convoys: the convoy row id. */
  battleId: bigint;
  side: 'a' | 'b';
  kind: 'fighter' | 'cruiser' | 'cargo' | 'escort' | 'hunter';
  x: number; y: number; z: number;
  /** Hit-sphere radius (m). */
  r: number;
  hp: number;
  hitByPlayer: boolean;
  /** The hull's scene object when the owner has one — impact decals ride it. */
  obj?: THREE.Object3D | null;
}

const store = (globalThis as unknown as { __atlasAirTargets?: Map<string, AirTarget> });
const targets: Map<string, AirTarget> = store.__atlasAirTargets ?? (store.__atlasAirTargets = new Map());

export function upsertAirTarget(t: AirTarget): void {
  const cur = targets.get(t.id);
  if (cur) { cur.x = t.x; cur.y = t.y; cur.z = t.z; cur.r = t.r; if (t.obj !== undefined) cur.obj = t.obj; }
  else targets.set(t.id, t);
}

export function removeAirTarget(id: string): void { targets.delete(id); }

export function clearAirTargets(): void { targets.clear(); }

/** Remove only the targets whose id starts with `prefix` — modules share ONE
 *  registry (bug sweep: WarAir's blanket clear() wiped CargoSky's freighter
 *  hulls mid-fight, resetting accumulated damage + escort hostility). */
export function clearAirTargetsByPrefix(prefix: string): void {
  for (const id of [...targets.keys()]) if (id.startsWith(prefix)) targets.delete(id);
}

export function airTargetList(): AirTarget[] { return [...targets.values()]; }

export function airTargetById(id: string): AirTarget | null { return targets.get(id) ?? null; }

/** The first live target whose hit sphere contains the point (margin widens it). */
export function airTargetAt(x: number, y: number, z: number, margin = 0): AirTarget | null {
  for (const t of targets.values()) {
    if (t.hp <= 0) continue;
    const dx = t.x - x, dy = t.y - y, dz = t.z - z;
    const rr = t.r + margin;
    if (dx * dx + dy * dy + dz * dz <= rr * rr) return t;
  }
  return null;
}

/** Apply player damage. Returns 'killed' exactly once (the hp crossing). */
export function damageAirTarget(id: string, dmg: number): 'hit' | 'killed' | 'dead' {
  const t = targets.get(id);
  if (!t || t.hp <= 0) return 'dead';
  t.hitByPlayer = true;
  t.hp -= dmg;
  return t.hp <= 0 ? 'killed' : 'hit';
}

/** Has the player fired on this side in this battle (the hostility latch)? */
export function sideHostile(battleId: bigint, side: 'a' | 'b'): boolean {
  for (const t of targets.values()) if (t.battleId === battleId && t.side === side && t.hitByPlayer) return true;
  return false;
}
